import { Temporal } from '@js-temporal/polyfill'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import {
  buildEncounterFilterQueryVariables,
  buildEncounterTableSortState,
  createEncounterSortUpdater,
  EncountersFiltersQueryParams,
  EncountersFiltersSortKeySchema,
} from '../../encounters/hooks/useEncounterFilters'
import {
  countMountedEncounterFilterConfigs,
  mountEncounterFilterConfigs,
  projectEncounterFiltersToMountedDefinitions,
  useEncounterFilterDefinitions,
  useWorkBlocksFilterConfigs,
} from '../../../components/encounters/encounterFilters'
import type {
  BaseEncounterFilterConfig,
  MountedEncounterFilterDefinition,
} from '../../../components/encounters/encounterFilters'
import type { UseWorkBlocksQueryParams } from '../hooks/useWorkBlocksQuery'
import type { components } from '@joyful/openapi/types.gen'
import { createEnumSchema } from '@joyful/utils'
import {
  createOperatorArrayDefault,
  parseAsOperatorArrayOf,
} from '../../filtering/utils/queryParams'
import {
  WorkBlockAssigneeRoleSchema,
  WorkBlockAssignmentCompletionSchema,
} from '../components/workBlocksTypes'

import {
  createSerializer,
  parseAsArrayOf,
  parseAsString,
  parseAsStringEnum,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs'
import type { SetValues, Values } from 'nuqs'
import { z } from 'zod'
import type { OnChangeFn, SortingState } from '../../../../core'
export const TODAY_ROUTE_VALUE = 'today'
export const RESERVE_WORK_BLOCK_QUERY_VALUE = 'RESERVE'

export const WORK_BLOCKS_PAGE_ACCESS_CAPABILITIES = [
  'encounter.work_blocks.view',
  'encounter.work_blocks.reserve.view',
] as const

export const WorkBlockAssignmentGroupBySchema = createEnumSchema<
  components['schemas']['WorkBlockAssignmentGroupBy']
>([
  { title: `Project`, value: 'project' },
  { title: `Workflow status`, value: 'workflow_status' },
  { title: `Owner`, value: 'owner' },
  { title: `Patient`, value: 'patient_name' },
  { title: `Payer`, value: 'payer' },
])

export const WorkBlocksViewSchema = createEnumSchema([
  { title: `List`, value: 'list' },
  { title: `Kanban`, value: 'kanban' },
])

export const WorkBlocksRouteQueryParams = {
  ...EncountersFiltersQueryParams,
  assigneeRoles: parseAsArrayOf(
    parseAsStringLiteral(WorkBlockAssigneeRoleSchema.values()),
  ).withDefault([]),
  assignmentCompletion: parseAsOperatorArrayOf(
    parseAsStringLiteral(WorkBlockAssignmentCompletionSchema.values()),
  ).withDefault(createOperatorArrayDefault()),
  billerUserId: parseAsString,
  group_by: parseAsStringLiteral(WorkBlockAssignmentGroupBySchema.values()),
  sortKey: parseAsStringEnum(EncountersFiltersSortKeySchema.options),
  sortOrder: parseAsStringEnum(['asc', 'desc'] as const).withDefault('asc'),
  view: parseAsStringLiteral(WorkBlocksViewSchema.values()).withDefault('list'),
}

export type WorkBlocksRouteQueryState = Values<
  typeof WorkBlocksRouteQueryParams
>
export type SetWorkBlocksRouteQueryParams = SetValues<
  typeof WorkBlocksRouteQueryParams
>

export const serializeWorkBlocksRouteQueryParams = createSerializer(
  WorkBlocksRouteQueryParams,
)

export function useWorkBlocksRouteQueryParams() {
  return useQueryStates(WorkBlocksRouteQueryParams, { history: 'push' })
}

export function useWorkBlocksMountedFilterConfigs(): Array<BaseEncounterFilterConfig> {
  const definitions = useEncounterFilterDefinitions()
  return [
    ...useWorkBlocksFilterConfigs(),
    definitions.tags,
    definitions.projects,
  ].filter(Boolean)
}

export function useWorkBlocksMountedFilterDefinitions() {
  return mountEncounterFilterConfigs(useWorkBlocksMountedFilterConfigs())
}

export function getEffectiveWorkBlocksGroupBy({
  groupBy,
  hasEncounterOwnership,
}: {
  groupBy: Values<typeof WorkBlocksRouteQueryParams>['group_by']
  hasEncounterOwnership: boolean
}) {
  if (hasEncounterOwnership && groupBy === 'workflow_status') {
    return 'owner'
  }

  if (!hasEncounterOwnership && groupBy === 'owner') {
    return 'workflow_status'
  }

  return groupBy
}

export function useWorkBlocksQueryParams(): Omit<
  UseWorkBlocksQueryParams,
  'workDate'
> {
  const [workBlocksRouteParams] = useWorkBlocksRouteQueryParams()
  const hasViewAllWorkBlocks = useHasUserCapability(
    'encounter.work_blocks.view_all_users',
  )
  const hasReserveView = useHasUserCapability(
    'encounter.work_blocks.reserve.view',
  )
  const filterDefinitions = useWorkBlocksMountedFilterDefinitions()
  const projectedEncounterFilters = projectEncounterFiltersToMountedDefinitions(
    {
      encounterFilters: workBlocksRouteParams,
      filterDefinitions,
    },
  )

  const encounterFilterVariables = buildEncounterFilterQueryVariables(
    projectedEncounterFilters,
  )

  return {
    blockersFilter: encounterFilterVariables.blockersFilter ?? undefined,
    dateEndFilter: encounterFilterVariables.dateEndFilter,
    dateStartFilter: encounterFilterVariables.dateStartFilter,
    groupBy: workBlocksRouteParams.group_by,
    lifecycleOutcomesFilter:
      encounterFilterVariables.lifecycleOutcomesFilter ?? undefined,
    lifecyclesFilter: encounterFilterVariables.lifecyclesFilter ?? undefined,
    ownersFilter: encounterFilterVariables.ownersFilter ?? undefined,
    patientsFilter: encounterFilterVariables.patientsFilter ?? undefined,
    payersFilter: encounterFilterVariables.payersFilter ?? undefined,
    projectsFilter: encounterFilterVariables.projectsFilter ?? undefined,
    projectsFilterOperator:
      encounterFilterVariables.projectsFilterOperator ?? undefined,
    providersFilter: encounterFilterVariables.providersFilter ?? undefined,
    query: encounterFilterVariables.query,
    tagsFilter: encounterFilterVariables.tagsFilter ?? undefined,
    tagsFilterOperator:
      encounterFilterVariables.tagsFilterOperator ?? undefined,
    ...getWorkBlocksQueryScope({
      billerUserId: workBlocksRouteParams.billerUserId,
      hasReserveView,
      hasViewAllWorkBlocks,
    }),
    sortKey: workBlocksRouteParams.sortKey,
    sortOrder: workBlocksRouteParams.sortOrder,
    workflowStatusesFilter:
      encounterFilterVariables.workflowStatusesFilter ?? undefined,
    workflowStatusesSimplifiedFilter:
      encounterFilterVariables.workflowStatusesSimplifiedFilter ?? undefined,
  }
}

export function countWorkBlockEncounterFilters(
  filters: Values<typeof WorkBlocksRouteQueryParams>,
  filterDefinitions: Array<MountedEncounterFilterDefinition>,
) {
  return countMountedEncounterFilterConfigs({
    encounterFilters: filters,
    filterDefinitions,
  })
}

export function getWorkBlocksQueryScope({
  billerUserId,
  hasReserveView,
  hasViewAllWorkBlocks,
}: {
  billerUserId: string | null
  hasReserveView: boolean
  hasViewAllWorkBlocks: boolean
}): Pick<UseWorkBlocksQueryParams, 'queryBillerUserId' | 'workBlockKind'> {
  if (billerUserId === RESERVE_WORK_BLOCK_QUERY_VALUE) {
    if (hasViewAllWorkBlocks || hasReserveView) {
      return { queryBillerUserId: null, workBlockKind: 'RESERVE' }
    }

    return { queryBillerUserId: null, workBlockKind: null }
  }

  if (!hasViewAllWorkBlocks) {
    return { queryBillerUserId: null, workBlockKind: null }
  }

  return { queryBillerUserId: billerUserId, workBlockKind: null }
}

export function useWorkBlocksSort(): {
  onSortingChange: OnChangeFn<SortingState>
  sorting: SortingState
} {
  const [params, setParams] = useWorkBlocksRouteQueryParams()
  const onSortingChange: OnChangeFn<SortingState> = (sorting) => {
    void setParams(createEncounterSortUpdater(sorting))
  }
  return {
    onSortingChange,
    sorting: buildEncounterTableSortState(params),
  }
}

// Returns `null` for the magic `'today'` segment so downstream queries can
// omit `work_date` and let the backend resolve it via `business_today()`
// (ET) rather than the browser clock. Returns `undefined` for an unparseable
// segment so the route can redirect.
const QueueWorkDateRouteParamSchema = z
  .union([z.literal(TODAY_ROUTE_VALUE), z.iso.date()])
  .transform((value): Temporal.PlainDate | null => {
    if (value === TODAY_ROUTE_VALUE) {
      return null
    }

    return Temporal.PlainDate.from(value)
  })

export function parseQueueWorkDate(
  value: string | undefined,
): Temporal.PlainDate | null | undefined {
  const parsed = QueueWorkDateRouteParamSchema.safeParse(value)
  return parsed.success ? parsed.data : undefined
}
